<template>
    <DefaultLayout :meta="meta">

        <div class="w-full bg-gray-50">

            <div class="w-full max-w-screen-xl mx-auto px-6 py-6">

                <h1 class="font-bold text-center">
                    {{ __('home::checkout.cart') }}
                </h1>

            </div>

        </div>

        <div class="w-full max-w-screen-xl mx-auto px-6 py-6">

            <div class="grid lg:grid-cols-5 lg:gap-16">
                <div class="order-2 lg:order-1 lg:col-span-3 bg-white py-10">
                    <div class="flex justify-between border-b pb-4">
                        <h1 class="font-semibold text-2xl">{{ __('home::checkout.products') }}</h1>
                    </div>
                    <div class="hidden sm:flex mt-10 mb-5">
                        <h3 class="font-semibold text-gray-600 text-xs uppercase w-3/5">
                            {{ __('home::checkout.product') }}
                        </h3>

                        <h3 class="font-semibold text-gray-600 text-xs uppercase w-1/5 text-center">
                            {{ __('home::checkout.quantity') }}
                        </h3>
                        <h3 class="font-semibold text-gray-600 text-xs uppercase w-1/5 text-right">
                            {{ __('home::checkout.price') }}
                        </h3>
                    </div>

                    <div
                        class="sm:flex sm:items-center py-4 sm:py-2"
                        v-for="line in order.lines"
                        :key="line.id"
                    >
                        <div class="flex sm:w-3/5 mb-4 sm:mb-0">
                            <div class="w-16 sm:w-24">
                                <img class="h-auto" :src="line.photo ? line.photo.final_thumb_path : line.product.primary_image.public_path" alt="">
                            </div>

                            <div class="flex flex-col justify-between ml-4 flex-grow py-1 sm:py-3">
                                <div>
                                    <div class="flex items-center gap-2">
                                        <span class="font-bold text-sm">{{ line.product.name }}</span>

                                        <div
                                            class="px-2 py-0.5 text-xs rounded-full shadow-sm font-bold text-center"
                                            :class="[$page.props.__site.promotion_style === 'black_friday'
                                            ? 'text-black bg-[#ffd732]'
                                            : 'text-primary bg-primary-light']"
                                            v-if="$page.props.__site.promotion_product_line"
                                            v-html="$page.props.__site.promotion_product_line"
                                        />
                                    </div>
                                    <div class="text-xs mt-1">{{ line.product.size_info }}</div>
                                </div>
                                <button type="button" class="font-semibold hover:text-red-500 text-gray-500 text-xs text-left mt-2" @click="modifyQuantity(line, 0)">
                                    {{ __('forms::actions.remove') }}
                                </button>
                            </div>
                        </div>

                        <div class="sm:w-2/5 flex items-center justify-end bg-gray-50 p-2 sm:bg-transparent sm:p-0">
                            <div class="flex sm:w-1/2 justify-end sm:justify-center items-center order-1 sm:order-0">
                                <button
                                    type="button"
                                    class="bg-gray-100 rounded-full w-7 h-7 p-1"
                                    @click="modifyQuantity(line, line.quantity - 1)"
                                >
                                    <MinusIcon class="text-gray-600 w-5 h-5" />
                                </button>

                                <Input type="text" class="w-12 text-center mx-2" :value="line.quantity" @change="modifyQuantity(line, $event.target.value)" />

                                <button
                                    type="button"
                                    class="bg-gray-100 rounded-full w-7 h-7 p-1"
                                    @click="modifyQuantity(line, line.quantity + 1)"
                                >
                                    <PlusIcon class="text-gray-600 w-5 h-5" />
                                </button>
                            </div>

                            <div class="text-right sm:w-1/2 font-semibold text-sm order-0 sm:order-1 pr-6 sm:pr-0">
                                {{ formatPrice(line.total, order.currency) }}
                            </div>
                        </div>
                    </div>

                    <InertiaLink :href="$route('home::orders.create')" class="font-semibold text-gray-600 text-sm mt-10 inline-block">
                        <ArrowNarrowLeftIcon class="inline-block mr-2 text-gray-600 w-4" />
                        {{ __('home::checkout.back_to_editor') }}
                    </InertiaLink>
                </div>

                <div class="order-1 lg:order-2 lg:col-span-2 px-8 py-10 bg-gray-50">
                    <h1 class="font-semibold text-2xl border-b pb-4">
                        {{ __('home::checkout.summary') }}
                    </h1>

                    <div class="mt-6">
                        <div class="flex justify-between py-1 text-sm uppercase" v-if="order.delivery_method">
                            <span>{{ __('home::checkout.delivery') }}</span>
                            <span>{{ formatPrice(order.delivery_method.prices[order.currency] * (1 + order.vat_rate), order.currency) }}</span>
                        </div>
                        <div class="mt-2" v-if="order.discount > 0">
                            <div class="flex italic justify-between py-1 text-sm uppercase">
                                <span>{{ __('home::checkout.total_before_discount') }}</span>
                                <span>{{ formatPrice(order.total + order.discount, order.currency) }}</span>
                            </div>
                            <div class="flex justify-between py-1 text-sm uppercase" v-if="order.discount > 0">
                                <span>{{ __('home::checkout.discount') }}</span>
                                <span>-{{ formatPrice(order.discount, order.currency) }}</span>
                            </div>
                        </div>
                        <!--
                        <div class="flex justify-between py-1 text-sm uppercase mt-2">
                            <span>{{ __('home::checkout.subtotal') }}</span>
                            <span>{{ formatPrice(order.subtotal, order.currency) }}</span>
                        </div>
                        <div class="flex justify-between py-1 text-sm uppercase">
                            <span>{{ __('home::checkout.vat') }}</span>
                            <span>{{ formatPrice(order.vat, order.currency) }}</span>
                        </div>
                        -->
                        <div class="flex font-semibold justify-between py-1 text-sm uppercase mt-2 mb-4">
                            <span>{{ __('home::checkout.total_incl_vat') }}</span>
                            <span>{{ formatPrice(order.total, order.currency) }}</span>
                        </div>

                        <div class="px-4 py-2 rounded-sm shadow-sm text-xs lg:text-sm text-primary bg-primary-light text-center mt-2" v-if="delayedPaymentDays">
                            {{ __('home::orders.pay_later_terms', { days: delayedPaymentDays }) }}
                        </div>

                        <div class="px-4 py-2 rounded-sm shadow-sm text-xs lg:text-sm text-primary bg-primary-light text-center mt-2" v-if="productCount < 4">
                            {{ __('home::orders.free_delivery_info', { count: 4 - productCount }) }}
                        </div>

                        <CTA :href="$route('home::checkout.billing')" class="w-full mt-2 rounded-sm" variant="large">
                            {{ __('home::checkout.checkout_cta') }}
                            <ArrowNarrowRightIcon class="h-w w-4 ml-1" />
                        </CTA>

                        <div class="mt-4 text-center">
                            <img src="/media/payment-cards.png" class="inline-block h-8" v-if="$page.props.__site.payment_methods.includes('card')" />
                            <img src="/media/payment-mobilepay.png" class="inline-block h-8" v-if="$page.props.__site.payment_methods.includes('mobilepay')" />
                            <img src="/media/payment-vipps.png" class="inline-block h-8 px-2" v-if="$page.props.__site.payment_methods.includes('vipps')" />
                        </div>

                        <div class="border-t mt-6 pt-6 mb-3">
                            <label class="font-semibold inline-block mb-3 text-sm uppercase">
                                {{ __('home::checkout.coupon_code') }}
                            </label>
                            <Input type="text" name="coupon" :placeholder="__('home::checkout.enter_coupon_code')" v-model="coupon" />
                            <div class="text-red-500 font-semibold my-3" v-if="couponError">
                                {{ __('home::checkout.invalid_coupon_code') }}
                            </div>
                            <div class="text-green-700 font-semibold my-3" v-if="couponSuccess">
                                {{ __('home::checkout.coupon_applied') }}
                            </div>
                        </div>

                        <button class="bg-gray-700 hover:bg-gray-900 px-5 py-2 text-sm text-white uppercase" @click="applyCoupon">
                            {{ __('home::actions.apply') }}
                        </button>

                    </div>
                </div>

            </div>


        </div>

    </DefaultLayout>
</template>

<script>
    import collect from 'collect.js'
    import axios from 'axios'

    import {
        ArrowNarrowLeftIcon,
        ArrowNarrowRightIcon,
        ChevronRightIcon,
        PlusIcon,
        MinusIcon,
    } from '@heroicons/vue/solid'

    export default {
        components: {
            ArrowNarrowLeftIcon,
            ArrowNarrowRightIcon,
            ChevronRightIcon,
            PlusIcon,
            MinusIcon,
        },
        props: {
            meta: Object,
            data: Object,
            dataLayer: Object,
            delayedPaymentDaysInitial: Number|String,
        },
        data() {
            return {
                order: this.data,
                coupon: null,
                couponError: false,
                couponSuccess: false,
                delayedPaymentDays: this.delayedPaymentDaysInitial,
            }
        },
        computed: {
            productCount: function () {
                return collect(this.order.lines)
                    .sum((item) => {
                        return item.quantity;
                    });
            },
        },
        mounted() {
            this.$gtm.trackEvent(this.dataLayer);
        },
        methods: {
            modifyQuantity(line, quantity) {

                let request = {
                    line_id: line.id,
                    quantity: quantity,
                };

                axios
                    .post(this.$route('home::checkout.update-quantity'), request)
                    .then((res) => {
                        this.order = res.data;
                    });
            },
            applyCoupon() {

                if (this.coupon == "") {
                    return;
                }

                this.couponError = false;
                this.couponSuccess = false;

                let request = {
                    coupon: this.coupon,
                };

                axios
                    .post(this.$route('home::checkout.apply-coupon'), request)
                    .then((res) => {
                        this.order = res.data;
                        this.delayedPaymentDays = this.order.coupons[0].delayed_payment_days || this.delayedPaymentDays;
                        this.couponSuccess = true;
                    })
                    .catch((error) => {
                        this.couponError = true;
                    });
            },
        },
    }
</script>
